import { MoreVertical, TrendingDown, TrendingUp } from "lucide-react";
import React from "react";
import InnerLoader from "../ui/loader/inner-loader";
import DataNotFound from "../ui/loader/data-not-found";
import { TotalHeadCount } from "../icons/user-dashboard";

export function MetricCard({
    icon,
    iconBg,
    iconColor,
    badge,
    value,
    label,
    progress,
    progressColor,
    valueColor,
    bg,
    statsBadge,
    isProgress = false,
    isBadge = false,
}) {
    const Icon = icon;
    const badgeStyles = {
        up: { bg: '#f0fdf4', color: '#16a34a' },
        down: { bg: '#fef2f2', color: '#dc2626' },
        neutral: { bg: '#f5f5f4', color: '#78716c' },
        warning: { bg: '#fffbeb', color: '#d97706' },
    };

    return (
        <div
            style={{
                backgroundColor: bg ? bg : "#fff",
                borderRadius: 16,
                padding: "22px 24px",
                display: "flex",
                flexDirection: "column",
                gap: 5,
                border: "1px solid #f1f5f9",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                cursor: "default",
                flex: 1,
                minWidth: 200,
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)";
                e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            <div className="flex items-center justify-between mb-2.5">
                <div className="flex gap-2 items-center">
                    <div className="w-[34px] h-[34px] rounded-[5px] flex items-center justify-center shrink-0 bg-blend-darken" style={{ background: iconBg }}>
                        {React.cloneElement(Icon, {
                            className: 'w-5 h-5',
                            style: { color: iconColor },
                            strokeWidth: 2,
                        })}
                    </div>

                    <div className="text-[16px] text-[#000] font-semibold">{label}</div>
                </div>

                {isBadge && (
                    <div
                        className="flex items-center gap-1 text-[11px] font-bold rounded-md px-2 py-1"
                        style={{ background: badgeStyles[badge.type].bg, color: badgeStyles[badge.type].color }}
                    >
                        {badge.type === 'up' && <TrendingUp className="w-2.5 h-2.5" strokeWidth={3} />}
                        {badge.type === 'down' && <TrendingDown className="w-2.5 h-2.5" strokeWidth={3} />}
                        {badge.text}
                    </div>
                )}
            </div>

            <div
                className="text-[28px] font-extrabold tracking-tight leading-none"
                style={{ color: valueColor || '#1a1a1a' }}
            >
                {value}
            </div>
            {statsBadge ? <div className="text-[12px] text-[#8a8480] font-medium mt-1.5">{statsBadge}</div> : <span></span>}

            {isProgress && (
                <div className="mt-3 h-1.5 bg-[rgba(0,0,0,0.07)] rounded-full overflow-hidden">
                    <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{ width: `${Math.max(progress, 2)}%`, background: progressColor }}
                    />
                </div>
            )}
        </div>
    );
}

export function DashboardCard({
    children,
    heading = "Card Heading",
    subHeading = null,
    Icon = null,
    headerRight = null,
    moreOff = true,
    loading = true,
    condition = true,
    DataNotFoundText,
    footer = null,
    iconColor = null,
    height = null,
    className = null,
}) {
    return (
        <div
            className={`
            bg-white/80 shadow-card rounded-[10px] 
                border border-[#e1e4ea] 
                py-7 gap-5 w-full h-full 
                flex flex-col justify-between
                [scrollbar-width:none]
                [&::-webkit-scrollbar]:hidden
                [&_*::-webkit-scrollbar]:hidden
                [&_*]:[scrollbar-width:none]
                ${className}  
            `}
        >
            {/* Main Content */}
            <div className="flex flex-col gap-0 overflow-hidden w-full h-full">
                {/* HEADER */}
                <div className="space-y-2.5 pb-2.5 border-b border-[rgba(82,88,102,0.20)] px-7">
                    <div className="flex items-center justify-between">
                        <h3 className="text-base font-bold text-[#0E121B] flex items-center gap-[10px] [&[fill]]:fill-current [&[stroke]]:stroke-current">
                            <div className={`${iconColor ? iconColor : "bg-theme-green-light text-theme-green"} px-2 py-2 rounded-md`}>
                                {Icon ? Icon : <TotalHeadCount />}
                            </div>
                            {heading}
                        </h3>
                        <div className="flex items-center gap-3">
                            {headerRight ? headerRight : null}
                            {moreOff ? <button className="text-[#A4A7AE]"><MoreVertical className="w-5 h-5" /></button> : null}
                        </div>
                    </div>
                    {subHeading ? <p className="text-xs text-[#535862] pb-1.5">{subHeading}</p> : null}
                </div>

                {/* BODY */}
                {loading ? (
                    <div
                        className="flex h-full w-full items-center justify-center"
                        style={{ height: height }}
                    >
                        <InnerLoader />
                    </div>
                ) : (
                    condition ? (
                        children
                    ) : (
                        <div
                            className="w-full h-full flex items-center justify-center"
                            style={{ height: height }}
                        >
                            <DataNotFound title={DataNotFoundText} />
                        </div>
                    )
                )}
            </div>

            {/* FOOTER */}
            {footer ? footer : null}
        </div>
    )
}